/**
 * A code generation friendly representation of FHIR StructureDefinition and
 * FHIR Schema designed to simplify SDK resource classes/types generation.
 */

import { createHash } from "node:crypto";
import type * as FS from "@atomic-ehr/fhirschema";
import type { StructureDefinition, ValueSet, ValueSetCompose } from "@root/fhir-types/hl7-fhir-r4-core";

export type Name = string & { readonly __brand: unique symbol };
export type CanonicalUrl = string & { readonly __brand: unique symbol };

const LEADING_DIGIT_RE = /^\d/;

export const extractNameFromCanonical = (canonical: CanonicalUrl, dropFragment = true) => {
    let localName = canonical.split("/").pop();
    if (!localName) return undefined;
    if (dropFragment && localName.includes("#")) {
        localName = localName.split("#")[0];
    }
    if (!localName) return undefined;
    if (LEADING_DIGIT_RE.test(localName)) {
        localName = `number_${localName}`;
    }
    return localName;
};

export type ValueConstraint = {
    kind: "pattern" | "fixed";
    type: string;
    value: FS.FHIRValue;
};

export type PkgName = string;
export type PkgVersion = string;

export interface PackageMeta {
    name: PkgName;
    version: PkgVersion;
}

export const packageMeta = (schema: TypeSchema): PackageMeta => {
    return {
        name: schema.identifier.package,
        version: schema.identifier.version,
    };
};
export const packageMetaToFhir = (packageMeta: PackageMeta) => `${packageMeta.name}#${packageMeta.version}`;
export const packageMetaToNpm = (packageMeta: PackageMeta) => `${packageMeta.name}@${packageMeta.version}`;

export const hashSchema = (schema: TypeSchema): string => {
    const json = JSON.stringify(schema);
    return createHash("sha256").update(json).digest("hex").slice(0, 16);
};

export type RichStructureDefinition = Omit<StructureDefinition, "url"> & {
    package_name: PkgName;
    package_version: PkgVersion;
    url: CanonicalUrl;
};

export type FHIRSchemaKind = "primitive-type" | "complex-type" | "resource" | "logical";

type RichFHIRSchemaBase = Omit<FS.FHIRSchema, "package_meta" | "base" | "name" | "url" | "derivation" | "kind"> & {
    package_meta: PackageMeta;
    name: Name;
    url: CanonicalUrl;
    base: CanonicalUrl;
    kind: FHIRSchemaKind;
};

export type RichProfileFHIRSchema = RichFHIRSchemaBase & { derivation: "constraint" };

export type RichPrimitiveFHIRSchema = RichFHIRSchemaBase & { derivation: "specialization"; kind: "primitive-type" };
export type RichComplexTypeFHIRSchema = RichFHIRSchemaBase & { derivation: "specialization"; kind: "complex-type" };
export type RichResourceFHIRSchema = RichFHIRSchemaBase & { derivation: "specialization"; kind: "resource" };
export type RichLogicalFHIRSchema = RichFHIRSchemaBase & { derivation: "specialization"; kind: "logical" };
export type RichSpecializationFHIRSchema =
    | RichPrimitiveFHIRSchema
    | RichComplexTypeFHIRSchema
    | RichResourceFHIRSchema
    | RichLogicalFHIRSchema;

export type RichFHIRSchema = RichProfileFHIRSchema | RichSpecializationFHIRSchema;

export const enrichFHIRSchema = (schema: FS.FHIRSchema, packageMeta: PackageMeta): RichFHIRSchema => {
    const derivation = schema.derivation === "constraint" ? ("constraint" as const) : ("specialization" as const);
    return {
        ...schema,
        derivation,
        kind: schema.kind as FHIRSchemaKind,
        package_meta: schema.package_meta || packageMeta,
        name: schema.name as Name,
        url: schema.url as CanonicalUrl,
        base: schema.base as CanonicalUrl,
    };
};

type IdentifierBase = {
    name: Name;
    url: CanonicalUrl;
    package: PkgName;
    version: PkgVersion;
};

export type PrimitiveIdentifier = { kind: "primitive-type" } & IdentifierBase;
export type ComplexTypeIdentifier = { kind: "complex-type" } & IdentifierBase;
export type ResourceIdentifier = { kind: "resource" } & IdentifierBase;
export type ValueSetIdentifier = { kind: "value-set" } & IdentifierBase;
export type NestedIdentifier = { kind: "nested" } & IdentifierBase;
export type BindingIdentifier = { kind: "binding" } & IdentifierBase;
export type ProfileIdentifier = { kind: "profile" } & IdentifierBase;
export type SnapshotProfileIdentifier = { kind: "profile-snapshot" } & IdentifierBase;
export type LogicalIdentifier = { kind: "logical" } & IdentifierBase;

export type Identifier =
    | PrimitiveIdentifier
    | ComplexTypeIdentifier
    | ResourceIdentifier
    | BindingIdentifier
    | ValueSetIdentifier
    | ProfileIdentifier
    | SnapshotProfileIdentifier
    | LogicalIdentifier;

export type TypeIdentifier = Identifier | NestedIdentifier;

export const isResourceIdentifier = (id: TypeIdentifier | undefined): id is ResourceIdentifier => {
    return id?.kind === "resource";
};

export const isLogicalIdentifier = (id: TypeIdentifier | undefined): id is LogicalIdentifier => {
    return id?.kind === "logical";
};

export const isComplexTypeIdentifier = (id: TypeIdentifier | undefined): id is ComplexTypeIdentifier => {
    return id?.kind === "complex-type";
};

export const isPrimitiveIdentifier = (id: TypeIdentifier | undefined): id is PrimitiveIdentifier => {
    return id?.kind === "primitive-type";
};

export const isNestedIdentifier = (id: TypeIdentifier | undefined): id is NestedIdentifier => {
    return id?.kind === "nested";
};

export const isProfileIdentifier = (id: TypeIdentifier | undefined): id is ProfileIdentifier => {
    return id?.kind === "profile";
};

export const isSnapshotProfileIdentifier = (id: TypeIdentifier | undefined): id is SnapshotProfileIdentifier => {
    return id?.kind === "profile-snapshot";
};

export const snapshotIdentifier = (id: ProfileIdentifier): SnapshotProfileIdentifier => ({
    ...id,
    kind: "profile-snapshot",
});

export const isSpecializationIdentifier = (
    id: TypeIdentifier | undefined,
): id is ResourceIdentifier | ComplexTypeIdentifier | LogicalIdentifier => {
    return isResourceIdentifier(id) || isComplexTypeIdentifier(id) || isLogicalIdentifier(id);
};

export const concatIdentifiers = <T extends TypeIdentifier = TypeIdentifier>(
    ...sources: (T[] | undefined)[]
): T[] | undefined => {
    const entries = sources
        .filter((s): s is T[] => s !== undefined)
        .flatMap((s) => s.map((id): [string, T] => [id.url, id]));
    if (entries.length === 0) return undefined;
    const deduped = Object.values(Object.fromEntries(entries) as Record<string, T>);
    return deduped.sort((a, b) => a.url.localeCompare(b.url));
};

export type TypeSchema =
    | SpecializationTypeSchema
    | PrimitiveTypeSchema
    | ValueSetTypeSchema
    | BindingTypeSchema
    | ProfileTypeSchema
    | SnapshotProfileTypeSchema;

type TypeSchemaGuardInput = TypeSchema | NestedTypeSchema | undefined;

export const isNestedTypeSchema = (schema: TypeSchemaGuardInput): schema is NestedTypeSchema => {
    return schema !== undefined && isNestedIdentifier(schema.identifier);
};

export const isSpecializationTypeSchema = (schema: TypeSchemaGuardInput): schema is SpecializationTypeSchema => {
    return (
        schema?.identifier.kind === "resource" ||
        schema?.identifier.kind === "complex-type" ||
        schema?.identifier.kind === "logical"
    );
};

export const isComplexTypeTypeSchema = (schema: TypeSchemaGuardInput): schema is ComplexTypeTypeSchema => {
    return schema?.identifier.kind === "complex-type";
};

export const isResourceTypeSchema = (schema: TypeSchemaGuardInput): schema is ResourceTypeSchema => {
    return schema?.identifier.kind === "resource";
};

export const isPrimitiveTypeSchema = (schema: TypeSchemaGuardInput): schema is PrimitiveTypeSchema => {
    return schema?.identifier.kind === "primitive-type";
};

export const isLogicalTypeSchema = (schema: TypeSchemaGuardInput): schema is LogicalTypeSchema => {
    return schema?.identifier.kind === "logical";
};

export const isProfileTypeSchema = (schema: TypeSchemaGuardInput): schema is ProfileTypeSchema => {
    return schema?.identifier.kind === "profile";
};

export const isBindingSchema = (schema: TypeSchemaGuardInput): schema is BindingTypeSchema => {
    return schema?.identifier.kind === "binding";
};

export const isValueSetTypeSchema = (schema: TypeSchemaGuardInput): schema is ValueSetTypeSchema => {
    return schema?.identifier.kind === "value-set";
};

export interface PrimitiveTypeSchema {
    identifier: PrimitiveIdentifier;
    description?: string;
    base: TypeIdentifier;
    dependencies?: TypeIdentifier[];
}

export type GenericParam = {
    typeVar: string;
    constraint: TypeIdentifier;
    /** Path from this schema down to the typeFamily-rooted field that introduces the param.
     *  Single segment for a direct introduce (e.g. `["outcome"]` on `BundleEntryResponse`);
     *  carrier fields are prepended as the param surfaces through passthrough (e.g.
     *  `["response", "outcome"]` on `BundleEntry`, `["entry", "response", "outcome"]` on
     *  `Bundle`). Used to align passthrough args across nesting hops. */
    path: string[];
};

/** Generic params a schema exposes — populated during index build (after
 *  `populateTypeFamily`). Each param either binds directly to a field whose type is a
 *  type-family root (e.g. `BundleEntry.resource: Resource` → T extends Resource) or
 *  is inherited from a generic-bearing field's target (passthrough). */
export type GenericInfo = {
    params: GenericParam[];
};

export interface NestedTypeSchema {
    identifier: NestedIdentifier;
    base: TypeIdentifier;
    fields: Record<string, Field>;
    /** Slicing definitions keyed by field name, kept apart from `fields` */
    slicing?: Record<string, FieldSlicing>;
    generic?: GenericInfo;
}

export interface ProfileTypeSchema {
    identifier: ProfileIdentifier;
    base: TypeIdentifier;
    description?: string;
    fields?: Record<string, Field>;
    /** Slicing definitions keyed by field name, kept apart from `fields` */
    slicing?: Record<string, FieldSlicing>;
    extensions?: ProfileExtension[];
    dependencies?: TypeIdentifier[];
    nested?: NestedTypeSchema[];
}

/** Flattened profile. */
export interface SnapshotProfileTypeSchema {
    identifier: SnapshotProfileIdentifier;
    base: TypeIdentifier;
    description?: string;
    fields: Record<string, Field>;
    /** Slicing definitions keyed by field name, kept apart from `fields` */
    slicing?: Record<string, FieldSlicing>;
    /** Top-level required field names inherited from the base resource that the
     *  profile chain does not re-state in its differential. Needed so that
     *  validate() emits validateRequired() for base-FHIR cardinality (e.g. for
     *  Provenance.target / .recorded) when a profile leaves those fields
     *  untouched. Profiles that explicitly include the field in their
     *  differential are NOT listed here — their constraint is already in
     *  `fields` and the validator handles them via the regular path. */
    inheritedRequiredFields?: string[];
    extensions?: ProfileExtension[];
    dependencies?: TypeIdentifier[];
    nested?: NestedTypeSchema[];
}

export const isSnapshotProfileTypeSchema = (s: TypeSchemaGuardInput): s is SnapshotProfileTypeSchema => {
    return s?.identifier.kind === "profile-snapshot";
};

export type SliceDiscriminator = {
    type: "value" | "exists" | "pattern" | "type" | "profile" | "position";
    path: string;
};

export interface FieldSlicing {
    discriminator?: SliceDiscriminator[];
    rules?: "open" | "closed" | "openAtEnd";
    ordered?: boolean;
    slices?: Record<string, FieldSlice>;
}

/** Whether the slicing discriminates by type (`$this`/type — e.g. Bundle.entry.resource by resourceType) */
export const isTypeDiscriminated = (slicing: FieldSlicing | undefined): boolean =>
    slicing?.discriminator?.some((d) => d.type === "type") ?? false;

export type ConstrainedChoiceInfo = {
    choiceBase: string;
    variant: string;
    variantType: TypeIdentifier;
    allChoiceNames: string[];
};

export type NameCandidates = {
    candidates: string[];
    recommended: string;
};

export interface FieldSlice {
    min?: number;
    max?: number;
    match?: Record<string, unknown>;
    required?: string[];
    excluded?: string[];
    elements?: string[];
    nameCandidates: NameCandidates;
}

export interface ExtensionSubField {
    name: string;
    url: string;
    valueFieldType?: TypeIdentifier;
    min?: number;
    max?: string;
}

export interface ProfileExtension {
    profile?: ProfileIdentifier;
    name: string;
    path: string;
    url?: string;
    min?: number;
    max?: string;
    mustSupport?: boolean;
    valueFieldTypes?: TypeIdentifier[];
    subExtensions?: ExtensionSubField[];
    isComplex?: boolean;
    nameCandidates: NameCandidates;
}

export const extractExtensionDeps = (ext: ProfileExtension): TypeIdentifier[] => [
    ...(ext.valueFieldTypes ?? []),
    ...(ext.profile ? [ext.profile] : []),
    ...(ext.subExtensions?.flatMap((sub) => (sub.valueFieldType ? [sub.valueFieldType] : [])) ?? []),
];

type SpecializationTypeSchemaBody = {
    base?: TypeIdentifier;
    description?: string;
    fields?: { [k: string]: Field };
    /** Slicing definitions keyed by field name, kept apart from `fields` */
    slicing?: Record<string, FieldSlicing>;
    nested?: NestedTypeSchema[];
    dependencies?: Identifier[];
    /** Transitive children grouped by kind (e.g. Resource → { resources: [DomainResource, Patient, …] }) */
    typeFamily?: TypeFamily;
    generic?: GenericInfo;
};

export type TypeFamily = {
    resources?: ResourceIdentifier[];
    complexTypes?: ComplexTypeIdentifier[];
};

export type ResourceTypeSchema = { identifier: ResourceIdentifier } & SpecializationTypeSchemaBody;
export type ComplexTypeTypeSchema = { identifier: ComplexTypeIdentifier } & SpecializationTypeSchemaBody;
export type LogicalTypeSchema = { identifier: LogicalIdentifier } & SpecializationTypeSchemaBody;

export type SpecializationTypeSchema = ResourceTypeSchema | ComplexTypeTypeSchema | LogicalTypeSchema;

export type FieldReference = {
    resource: TypeIdentifier[];
    profiles?: ProfileIdentifier[];
};

export interface RegularField {
    type: TypeIdentifier;
    reference?: FieldReference;
    required?: boolean;
    excluded?: boolean;
    array?: boolean;
    binding?: BindingIdentifier;
    enum?: EnumDefinition;
    min?: number;
    max?: number;
    valueConstraint?: ValueConstraint;
    mustSupport?: boolean;
}

export interface ChoiceFieldDeclaration {
    choices: string[];
    prohibited?: string[];
    required?: boolean;
    excluded?: boolean;
    array?: boolean;
    min?: number;
    max?: number;
}

export interface ChoiceFieldInstance {
    choiceOf: string;
    type: TypeIdentifier;
    required?: boolean;
    excluded?: boolean;
    array?: boolean;
    reference?: FieldReference;
    binding?: BindingIdentifier;
    enum?: EnumDefinition;
    min?: number;
    max?: number;
    valueConstraint?: ValueConstraint;
    mustSupport?: boolean;
}

export type Concept = {
    code: string;
    display?: string;
    system?: string;
};

export type EnumDefinition = {
    values: string[];
    isOpen: boolean;
};

export interface ValueSetTypeSchema {
    identifier: ValueSetIdentifier;
    description?: string;
    concept?: Concept[];
    compose?: ValueSetCompose;
}

export interface BindingTypeSchema {
    identifier: BindingIdentifier;
    description?: string;
    strength?: string;
    enum?: EnumDefinition;
    valueset?: ValueSetIdentifier;
    dependencies?: TypeIdentifier[];
}

export type Field = RegularField | ChoiceFieldDeclaration | ChoiceFieldInstance;

export const isNotChoiceDeclarationField = (field: Field | undefined): field is RegularField | ChoiceFieldInstance => {
    if (!field) return false;
    return (field as ChoiceFieldDeclaration).choices === undefined;
};

export const isChoiceDeclarationField = (field: Field | undefined): field is ChoiceFieldDeclaration => {
    if (!field) return false;
    return (field as ChoiceFieldDeclaration).choices !== undefined;
};

export const isChoiceInstanceField = (field: Field | undefined): field is ChoiceFieldInstance => {
    if (!field) return false;
    return (field as ChoiceFieldInstance).choiceOf !== undefined;
};

///////////////////////////////////////////////////////////
// ValueSet
///////////////////////////////////////////////////////////

export type RichValueSet = Omit<ValueSet, "name" | "url"> & {
    package_meta: PackageMeta;
    name: Name;
    url: CanonicalUrl;
};

export const enrichValueSet = (vs: ValueSet, packageMeta: PackageMeta): RichValueSet => {
    if (!vs.url) throw new Error("ValueSet must have a URL");
    if (!vs.name) throw new Error("ValueSet must have a name");
    return {
        ...vs,
        package_meta: (vs as RichValueSet).package_meta || packageMeta,
        name: vs.name as Name,
        url: vs.url as CanonicalUrl,
    };
};

///////////////////////////////////////////////////////////
